import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';

interface CampaignSummary {
  id: string;
  name: string;
  description: string | null;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
}

interface PerformanceSummary {
  leadCountByStatus: Record<string, number>;
  totalLeads: number;
  qualificationRate: number;
  opportunityCountByStatus: Record<string, number>;
  openPipelineValue: number;
  weightedForecast: number;
  winRate: number;
}

interface PortfolioRow {
  campaign: CampaignSummary;
  perf: PerformanceSummary | null;
}

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  ACTIVE: 'bg-green-900/40 text-green-200',
  PAUSED: 'bg-amber-900/40 text-amber-200',
  CLOSED: 'bg-blue-900/40 text-blue-200',
};

type SortKey = 'name' | 'leads' | 'pipeline' | 'winRate';

/**
 * Story 46.4 — portfolio view.
 *
 * Default landing for TENANT_ADMIN / SALES_MANAGER / PLATFORM_ADMIN. One
 * tile per campaign with KPIs from /campaigns/{id}/performance, drilling
 * into the campaign hub on click.
 */
export function PortfolioPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const isElevated =
    role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN' || role === 'SALES_MANAGER';
  const [rows, setRows] = useState<PortfolioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortKey>('leads');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<CampaignSummary[]>('/campaigns')
      .then(async ({ data }) => {
        if (cancelled) return;
        const filled = await Promise.all(
          data.map(async (c) => {
            try {
              const { data: perf } = await apiClient.get<PerformanceSummary>(
                `/campaigns/${c.id}/performance`,
              );
              return { campaign: c, perf };
            } catch {
              return { campaign: c, perf: null };
            }
          }),
        );
        if (!cancelled) setRows(filled);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;

  if (rows.length === 0) {
    return (
      <div className="p-6 space-y-4">
        <h1 className="text-2xl font-semibold">{t('portfolio.title')}</h1>
        <Card>
          <p className="text-sm text-muted-foreground">
            {t('portfolio.empty')}{' '}
            {isElevated && (
              <Link to="/campaigns" className="text-primary hover:underline">
                {t('portfolio.create_cta')}
              </Link>
            )}
          </p>
        </Card>
      </div>
    );
  }

  const visible = rows
    .filter((r) => statusFilter === 'all' || r.campaign.status === statusFilter)
    .sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.campaign.name.localeCompare(b.campaign.name);
        case 'leads':
          return (b.perf?.totalLeads ?? 0) - (a.perf?.totalLeads ?? 0);
        case 'pipeline':
          return (b.perf?.openPipelineValue ?? 0) - (a.perf?.openPipelineValue ?? 0);
        case 'winRate':
          return (b.perf?.winRate ?? 0) - (a.perf?.winRate ?? 0);
      }
    });

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('portfolio.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('portfolio.subtitle', { count: rows.length })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border bg-background px-3 py-2 text-sm"
          >
            <option value="all">{t('portfolio.filter_all')}</option>
            <option value="DRAFT">{t('common.draft')}</option>
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="PAUSED">{t('common.paused')}</option>
            <option value="CLOSED">{t('common.closed')}</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="rounded-md border bg-background px-3 py-2 text-sm"
          >
            <option value="leads">{t('portfolio.sort_leads')}</option>
            <option value="pipeline">{t('portfolio.sort_pipeline')}</option>
            <option value="winRate">{t('portfolio.sort_winRate')}</option>
            <option value="name">{t('portfolio.sort_name')}</option>
          </select>
          {isElevated && (
            <Link to="/campaigns">
              <Button size="sm" variant="outline">
                <Plus className="mr-1 h-4 w-4" /> {t('portfolio.new_campaign')}
              </Button>
            </Link>
          )}
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map(({ campaign, perf }) => (
          <Link
            key={campaign.id}
            to={`/campaigns/${campaign.id}`}
            className="block transition hover:scale-[1.01]"
          >
            <Card className="space-y-4">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-base font-semibold leading-tight">{campaign.name}</h2>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    STATUS_BADGE[campaign.status] ?? 'bg-muted'
                  }`}
                >
                  {campaign.status}
                </span>
              </div>

              {campaign.description && (
                <p className="line-clamp-2 text-xs text-muted-foreground">
                  {campaign.description}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat
                  label={t('portfolio.stat.leads')}
                  value={perf?.totalLeads.toLocaleString() ?? '—'}
                />
                <Stat
                  label={t('portfolio.stat.qual_rate')}
                  value={
                    perf
                      ? `${(perf.qualificationRate * 100).toFixed(0)}%`
                      : '—'
                  }
                />
                <Stat
                  label={t('portfolio.stat.open_opps')}
                  value={(perf?.opportunityCountByStatus.OPEN ?? 0).toString()}
                />
                <Stat
                  label={t('portfolio.stat.win_rate')}
                  value={
                    perf
                      ? `${(perf.winRate * 100).toFixed(0)}%`
                      : '—'
                  }
                />
              </div>

              {perf && perf.openPipelineValue > 0 && (
                <div className="border-t pt-2 text-xs">
                  <span className="text-muted-foreground">{t('portfolio.stat.open_pipeline')}: </span>
                  <span className="font-mono">
                    {perf.openPipelineValue.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0,
                    })}
                  </span>
                </div>
              )}
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-base font-semibold">{value}</div>
    </div>
  );
}
